import React, { useRef, useCallback, useMemo } from 'react';
import { Box, Typography, useTheme } from '@mui/material';
import { tokens } from '../../theme';
import {
  pixelToGrid,
  gridToSvg,
  generateGridLines,
  generateAxisLabels,
  formatCoordinate,
  decimalToFraction,
  getPolygonPath,
  defaultGridConfig,
} from './coordinateUtils';

/**
 * Base Coordinate Grid SVG Component
 * Renders the grid, axes, and all elements (points, lines, polygons, circles)
 */
const CoordinateGrid = ({
  config = defaultGridConfig,
  elements = [],
  pendingPoints = [],
  mode = 'point',
  readOnly = false,
  onGridClick,
  onElementClick,
  selectedId = null,
  highlightElements = [], // For showing correct answer overlay
  width = 400,
  height = 400,
  showCursor = true,
  scale = null, // Scale factor for element sizes (auto-calculated from width if null)
}) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const svgRef = useRef(null);
  const [cursorPosition, setCursorPosition] = React.useState(null);

  const gridConfig = useMemo(() => ({ ...defaultGridConfig, ...config }), [config]);

  // Calculate scale factor based on grid width (400px is the default/full size)
  const elementScale = useMemo(() => {
    if (scale !== null) return scale;
    return Math.min(width, height) / 400;
  }, [scale, width, height]);

  const { vertical: verticalLines, horizontal: horizontalLines } = useMemo(
    () => generateGridLines(gridConfig),
    [gridConfig]
  );

  const { xLabels, yLabels } = useMemo(
    () => generateAxisLabels(gridConfig),
    [gridConfig]
  );

  // A curve element flags `notToScale` when its y-axis was compressed to fit a
  // high-dynamic-range (high-degree) curve. In that case the numeric y tick labels
  // no longer match the drawn curve, so we suppress them and show a caption.
  const yNotToScale = useMemo(
    () => [...elements, ...highlightElements].some((el) => el && el.notToScale),
    [elements, highlightElements]
  );

  // Handle pointer/click events
  const handlePointerDown = useCallback(
    (e) => {
      if (readOnly || !onGridClick) return;

      const svg = svgRef.current;
      if (!svg) return;

      const rect = svg.getBoundingClientRect();
      const pixelX = e.clientX - rect.left;
      const pixelY = e.clientY - rect.top;

      const gridPos = pixelToGrid(pixelX, pixelY, gridConfig, width, height);
      onGridClick(gridPos);
    },
    [readOnly, onGridClick, gridConfig, width, height]
  );

  const handlePointerMove = useCallback(
    (e) => {
      if (!showCursor || readOnly) {
        setCursorPosition(null);
        return;
      }

      const svg = svgRef.current;
      if (!svg) return;

      const rect = svg.getBoundingClientRect();
      const pixelX = e.clientX - rect.left;
      const pixelY = e.clientY - rect.top;

      // Check if within bounds
      if (pixelX < 0 || pixelX > width || pixelY < 0 || pixelY > height) {
        setCursorPosition(null);
        return;
      }

      const gridPos = pixelToGrid(pixelX, pixelY, gridConfig, width, height);
      setCursorPosition(gridPos);
    },
    [showCursor, readOnly, gridConfig, width, height]
  );

  const handlePointerLeave = useCallback(() => {
    setCursorPosition(null);
  }, []);

  // Render a point element
  const renderPoint = useCallback(
    (point, isHighlight = false, index) => {
      const pos = gridToSvg(point.x, point.y, gridConfig, width, height);
      const isSelected = selectedId === point.id;
      const baseRadius = isSelected ? 12 : 10;
      const scaledRadius = baseRadius * elementScale;

      return (
        <g key={point.id || `point-${index}`}>
          <circle
            cx={pos.x}
            cy={pos.y}
            r={scaledRadius}
            fill={isHighlight ? colors.greenAccent[500] : colors.blueAccent[500]}
            stroke={isHighlight ? colors.greenAccent[300] : colors.blueAccent[300]}
            strokeWidth={Math.max(1, 2 * elementScale)}
            style={{
              cursor: readOnly ? 'default' : 'pointer',
              transition: 'r 0.1s',
            }}
            onClick={(e) => {
              e.stopPropagation();
              if (!readOnly && onElementClick) {
                onElementClick(point);
              }
            }}
          />
          {/* Coordinate label on hover/selected */}
          {(isSelected || isHighlight) && (
            <text
              x={pos.x}
              y={pos.y - 16 * elementScale}
              textAnchor="middle"
              fill={colors.grey[100]}
              fontSize={Math.max(8, 12 * elementScale)}
              fontFamily="monospace"
            >
              {formatCoordinate(point.x, point.y, point.xLabel, point.yLabel)}
            </text>
          )}
        </g>
      );
    },
    [gridConfig, width, height, colors, selectedId, readOnly, onElementClick, elementScale]
  );

  // Render a line element
  const renderLine = useCallback(
    (line, isHighlight = false, index) => {
      if (!line.points || line.points.length !== 2) return null;

      const start = gridToSvg(line.points[0].x, line.points[0].y, gridConfig, width, height);
      const end = gridToSvg(line.points[1].x, line.points[1].y, gridConfig, width, height);
      const isSelected = selectedId === line.id;
      const endpointRadius = 6 * elementScale;
      const lineWidth = (isSelected ? 4 : 3) * elementScale;

      return (
        <g key={line.id || `line-${index}`}>
          <line
            x1={start.x}
            y1={start.y}
            x2={end.x}
            y2={end.y}
            stroke={isHighlight ? colors.greenAccent[400] : colors.blueAccent[400]}
            strokeWidth={Math.max(1, lineWidth)}
            strokeLinecap="round"
            style={{ cursor: readOnly ? 'default' : 'pointer' }}
            onClick={(e) => {
              e.stopPropagation();
              if (!readOnly && onElementClick) {
                onElementClick(line);
              }
            }}
          />
          {/* Endpoint markers */}
          <circle
            cx={start.x}
            cy={start.y}
            r={endpointRadius}
            fill={isHighlight ? colors.greenAccent[500] : colors.blueAccent[500]}
          />
          <circle
            cx={end.x}
            cy={end.y}
            r={endpointRadius}
            fill={isHighlight ? colors.greenAccent[500] : colors.blueAccent[500]}
          />
        </g>
      );
    },
    [gridConfig, width, height, colors, selectedId, readOnly, onElementClick, elementScale]
  );

  // Render a polygon element
  const renderPolygon = useCallback(
    (polygon, isHighlight = false, index) => {
      if (!polygon.points || polygon.points.length < 3) return null;

      const path = getPolygonPath(polygon.points, gridConfig, width, height);
      const isSelected = selectedId === polygon.id;
      const vertexRadius = 5 * elementScale;
      const strokeWidth = (isSelected ? 4 : 3) * elementScale;

      return (
        <g key={polygon.id || `polygon-${index}`}>
          <path
            d={path}
            fill={
              isHighlight
                ? 'rgba(76, 206, 172, 0.3)'
                : 'rgba(78, 164, 229, 0.3)'
            }
            stroke={isHighlight ? colors.greenAccent[400] : colors.blueAccent[400]}
            strokeWidth={Math.max(1, strokeWidth)}
            style={{ cursor: readOnly ? 'default' : 'pointer' }}
            onClick={(e) => {
              e.stopPropagation();
              if (!readOnly && onElementClick) {
                onElementClick(polygon);
              }
            }}
          />
          {/* Vertex markers */}
          {polygon.points.map((p, i) => {
            const pos = gridToSvg(p.x, p.y, gridConfig, width, height);
            return (
              <circle
                key={i}
                cx={pos.x}
                cy={pos.y}
                r={vertexRadius}
                fill={isHighlight ? colors.greenAccent[500] : colors.blueAccent[500]}
              />
            );
          })}
        </g>
      );
    },
    [gridConfig, width, height, colors, selectedId, readOnly, onElementClick, elementScale]
  );

  // Render a circle element
  const renderCircle = useCallback(
    (circle, isHighlight = false, index) => {
      if (!circle.center || circle.radius === undefined) return null;

      const center = gridToSvg(circle.center.x, circle.center.y, gridConfig, width, height);
      // Calculate SVG radius (need to convert grid units to pixels)
      const edgePoint = gridToSvg(
        circle.center.x + circle.radius,
        circle.center.y,
        gridConfig,
        width,
        height
      );
      const svgRadius = edgePoint.x - center.x;
      const isSelected = selectedId === circle.id;
      const centerMarkerRadius = 5 * elementScale;
      const strokeWidth = (isSelected ? 4 : 3) * elementScale;

      return (
        <g key={circle.id || `circle-${index}`}>
          <circle
            cx={center.x}
            cy={center.y}
            r={Math.abs(svgRadius)}
            fill={
              isHighlight
                ? 'rgba(76, 206, 172, 0.3)'
                : 'rgba(78, 164, 229, 0.3)'
            }
            stroke={isHighlight ? colors.greenAccent[400] : colors.blueAccent[400]}
            strokeWidth={Math.max(1, strokeWidth)}
            style={{ cursor: readOnly ? 'default' : 'pointer' }}
            onClick={(e) => {
              e.stopPropagation();
              if (!readOnly && onElementClick) {
                onElementClick(circle);
              }
            }}
          />
          {/* Center marker */}
          <circle
            cx={center.x}
            cy={center.y}
            r={centerMarkerRadius}
            fill={isHighlight ? colors.greenAccent[500] : colors.blueAccent[500]}
          />
        </g>
      );
    },
    [gridConfig, width, height, colors, selectedId, readOnly, onElementClick, elementScale]
  );

  // Render a curve element
  const renderCurve = useCallback(
    (curve, isHighlight = false, index) => {
      if (!curve.points || curve.points.length < 2) return null;

      const isSelected = selectedId === curve.id;
      const strokeWidth = (isSelected ? 4 : 3) * elementScale;

      // Filter and convert points to SVG coordinates
      // We need to handle discontinuities by breaking the path
      const segments = [];
      let currentSegment = [];

      for (const p of curve.points) {
        // Check if point is within reasonable y bounds (with some padding)
        const yPadding = (gridConfig.yMax - gridConfig.yMin) * 2;
        if (p.y >= gridConfig.yMin - yPadding && p.y <= gridConfig.yMax + yPadding) {
          const svgPos = gridToSvg(p.x, p.y, gridConfig, width, height);
          currentSegment.push(svgPos);
        } else {
          // Discontinuity - start new segment
          if (currentSegment.length > 1) {
            segments.push(currentSegment);
          }
          currentSegment = [];
        }
      }
      // Don't forget the last segment
      if (currentSegment.length > 1) {
        segments.push(currentSegment);
      }

      if (segments.length === 0) return null;

      return (
        <g key={curve.id || `curve-${index}`}>
          {segments.map((segment, segIdx) => {
            const pathData = segment
              .map((pos, i) => `${i === 0 ? 'M' : 'L'} ${pos.x} ${pos.y}`)
              .join(' ');

            return (
              <path
                key={`${curve.id || index}-seg-${segIdx}`}
                d={pathData}
                fill="none"
                stroke={isHighlight ? colors.greenAccent[400] : colors.blueAccent[400]}
                strokeWidth={Math.max(1, strokeWidth)}
                strokeLinecap="round"
                strokeLinejoin="round"
                style={{ cursor: readOnly ? 'default' : 'pointer' }}
                onClick={(e) => {
                  e.stopPropagation();
                  if (!readOnly && onElementClick) {
                    onElementClick(curve);
                  }
                }}
              />
            );
          })}
          {/* Show equation label when selected */}
          {(isSelected || isHighlight) && curve.equation && (
            <text
              x={width / 2}
              y={20 * elementScale}
              textAnchor="middle"
              fill={colors.grey[100]}
              fontSize={Math.max(10, 12 * elementScale)}
              fontFamily="monospace"
            >
              {curve.equation}
            </text>
          )}
        </g>
      );
    },
    [gridConfig, width, height, colors, selectedId, readOnly, onElementClick, elementScale]
  );

  // Render an asymptote element (dashed vertical line with optional label)
  const renderAsymptote = useCallback(
    (asymptote, isHighlight = false, index) => {
      const x = asymptote.x;
      if (x < gridConfig.xMin || x > gridConfig.xMax) return null;

      const top = gridToSvg(x, gridConfig.yMax, gridConfig, width, height);
      const bottom = gridToSvg(x, gridConfig.yMin, gridConfig, width, height);
      const lineWidth = 1.5 * elementScale;
      const fontSize = 13;

      // Label at the top of the line
      const labelY = top.y + fontSize + 2;

      return (
        <g key={asymptote.id || `asymptote-${index}`}>
          <line
            x1={top.x}
            y1={top.y}
            x2={bottom.x}
            y2={bottom.y}
            stroke={isHighlight ? colors.greenAccent[400] : colors.grey[300]}
            strokeWidth={Math.max(0.5, lineWidth)}
            strokeDasharray={`${4 * elementScale},${3 * elementScale}`}
            opacity={0.6}
          />
          {asymptote.label && (
            <>
              <rect
                x={top.x - asymptote.label.length * 3.2}
                y={labelY - fontSize}
                width={asymptote.label.length * 6.4}
                height={fontSize + 4}
                fill={colors.primary[600]}
                rx={2}
              />
              <text
                x={top.x}
                y={labelY}
                textAnchor="middle"
                fill={colors.greenAccent[400]}
                fontSize={fontSize}
                fontFamily="monospace"
                fontWeight="bold"
              >
                {asymptote.label}
              </text>
            </>
          )}
        </g>
      );
    },
    [gridConfig, width, height, colors, elementScale]
  );

  // Render pending points (for line/polygon construction)
  const renderPendingPoints = useCallback(() => {
    if (pendingPoints.length === 0) return null;

    return (
      <g className="pending-points">
        {/* Lines connecting pending points */}
        {pendingPoints.length >= 1 && mode === 'polygon' && (
          <>
            {pendingPoints.map((p, i) => {
              if (i === 0) return null;
              const prev = gridToSvg(
                pendingPoints[i - 1].x,
                pendingPoints[i - 1].y,
                gridConfig,
                width,
                height
              );
              const curr = gridToSvg(p.x, p.y, gridConfig, width, height);
              return (
                <line
                  key={`pending-line-${i}`}
                  x1={prev.x}
                  y1={prev.y}
                  x2={curr.x}
                  y2={curr.y}
                  stroke={colors.grey[400]}
                  strokeWidth={2}
                  strokeDasharray="5,5"
                />
              );
            })}
            {/* Line from last point to cursor */}
            {cursorPosition && (
              <line
                x1={
                  gridToSvg(
                    pendingPoints[pendingPoints.length - 1].x,
                    pendingPoints[pendingPoints.length - 1].y,
                    gridConfig,
                    width,
                    height
                  ).x
                }
                y1={
                  gridToSvg(
                    pendingPoints[pendingPoints.length - 1].x,
                    pendingPoints[pendingPoints.length - 1].y,
                    gridConfig,
                    width,
                    height
                  ).y
                }
                x2={gridToSvg(cursorPosition.x, cursorPosition.y, gridConfig, width, height).x}
                y2={gridToSvg(cursorPosition.x, cursorPosition.y, gridConfig, width, height).y}
                stroke={colors.grey[400]}
                strokeWidth={2}
                strokeDasharray="5,5"
              />
            )}
          </>
        )}

        {/* Line mode - show line from first point to cursor */}
        {mode === 'line' && pendingPoints.length === 1 && cursorPosition && (
          <line
            x1={gridToSvg(pendingPoints[0].x, pendingPoints[0].y, gridConfig, width, height).x}
            y1={gridToSvg(pendingPoints[0].x, pendingPoints[0].y, gridConfig, width, height).y}
            x2={gridToSvg(cursorPosition.x, cursorPosition.y, gridConfig, width, height).x}
            y2={gridToSvg(cursorPosition.x, cursorPosition.y, gridConfig, width, height).y}
            stroke={colors.grey[400]}
            strokeWidth={2}
            strokeDasharray="5,5"
          />
        )}

        {/* Circle mode - show circle preview from center to cursor */}
        {mode === 'circle' && pendingPoints.length === 1 && cursorPosition && (
          <>
            {(() => {
              const center = gridToSvg(
                pendingPoints[0].x,
                pendingPoints[0].y,
                gridConfig,
                width,
                height
              );
              const cursor = gridToSvg(
                cursorPosition.x,
                cursorPosition.y,
                gridConfig,
                width,
                height
              );
              const radius = Math.sqrt(
                Math.pow(cursor.x - center.x, 2) + Math.pow(cursor.y - center.y, 2)
              );
              return (
                <circle
                  cx={center.x}
                  cy={center.y}
                  r={radius}
                  fill="rgba(128, 128, 128, 0.2)"
                  stroke={colors.grey[400]}
                  strokeWidth={2}
                  strokeDasharray="5,5"
                />
              );
            })()}
          </>
        )}

        {/* Pending point markers */}
        {pendingPoints.map((p, i) => {
          const pos = gridToSvg(p.x, p.y, gridConfig, width, height);
          return (
            <circle
              key={`pending-${i}`}
              cx={pos.x}
              cy={pos.y}
              r={8 * elementScale}
              fill={colors.grey[500]}
              stroke={colors.grey[300]}
              strokeWidth={Math.max(1, 2 * elementScale)}
            />
          );
        })}
      </g>
    );
  }, [pendingPoints, mode, cursorPosition, gridConfig, width, height, colors, elementScale]);

  // Render cursor position indicator
  const renderCursor = useCallback(() => {
    if (!cursorPosition || readOnly) return null;

    const pos = gridToSvg(cursorPosition.x, cursorPosition.y, gridConfig, width, height);
    const cursorRadius = 8 * elementScale;
    const cursorCrossSize = 12 * elementScale;

    return (
      <g className="cursor" pointerEvents="none">
        <circle cx={pos.x} cy={pos.y} r={cursorRadius} fill="none" stroke={colors.grey[400]} strokeWidth={1} />
        <line
          x1={pos.x - cursorCrossSize}
          y1={pos.y}
          x2={pos.x + cursorCrossSize}
          y2={pos.y}
          stroke={colors.grey[400]}
          strokeWidth={1}
        />
        <line
          x1={pos.x}
          y1={pos.y - cursorCrossSize}
          x2={pos.x}
          y2={pos.y + cursorCrossSize}
          stroke={colors.grey[400]}
          strokeWidth={1}
        />
      </g>
    );
  }, [cursorPosition, readOnly, gridConfig, width, height, colors, elementScale]);

  return (
    <Box>
      <svg
        ref={svgRef}
        width={width}
        height={height}
        style={{
          touchAction: 'none',
          cursor: readOnly ? 'default' : 'crosshair',
          backgroundColor: colors.primary[600],
          borderRadius: '4px',
        }}
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerLeave={handlePointerLeave}
      >
        {/* Grid lines */}
        {gridConfig.showGridLines && (
          <g className="gridlines">
            {verticalLines.map((x) => {
              const pos = gridToSvg(x, 0, gridConfig, width, height);
              return (
                <line
                  key={`v-${x}`}
                  x1={pos.x}
                  y1={0}
                  x2={pos.x}
                  y2={height}
                  stroke={x === 0 ? colors.grey[400] : colors.grey[700]}
                  strokeWidth={x === 0 ? 2 : 0.5}
                />
              );
            })}
            {horizontalLines.map((y) => {
              const pos = gridToSvg(0, y, gridConfig, width, height);
              return (
                <line
                  key={`h-${y}`}
                  x1={0}
                  y1={pos.y}
                  x2={width}
                  y2={pos.y}
                  stroke={y === 0 ? colors.grey[400] : colors.grey[700]}
                  strokeWidth={y === 0 ? 2 : 0.5}
                />
              );
            })}
          </g>
        )}

        {/* Axis labels */}
        {gridConfig.showAxisLabels && (
          <g className="axis-labels">
            {xLabels.map((x) => {
              const pos = gridToSvg(x, 0, gridConfig, width, height);
              return (
                <text
                  key={`xl-${x}`}
                  x={pos.x}
                  y={pos.y + 16}
                  textAnchor="middle"
                  fill={colors.grey[400]}
                  fontSize={10}
                  fontFamily="monospace"
                >
                  {decimalToFraction(x)}
                </text>
              );
            })}
            {!yNotToScale && yLabels.map((y) => {
              const pos = gridToSvg(0, y, gridConfig, width, height);
              return (
                <text
                  key={`yl-${y}`}
                  x={pos.x - 8}
                  y={pos.y + 4}
                  textAnchor="end"
                  fill={colors.grey[400]}
                  fontSize={10}
                  fontFamily="monospace"
                >
                  {decimalToFraction(y)}
                </text>
              );
            })}
            {yNotToScale && (
              <text
                x={4}
                y={12}
                textAnchor="start"
                fill={colors.grey[500]}
                fontSize={9}
                fontFamily="monospace"
                fontStyle="italic"
              >
                y not to scale
              </text>
            )}
            {/* Origin label */}
            <text
              x={gridToSvg(0, 0, gridConfig, width, height).x - 8}
              y={gridToSvg(0, 0, gridConfig, width, height).y + 16}
              textAnchor="end"
              fill={colors.grey[400]}
              fontSize={10}
              fontFamily="monospace"
            >
              0
            </text>
          </g>
        )}

        {/* Highlight elements (correct answer overlay) */}
        <g className="highlights">
          {highlightElements.map((el, i) => {
            switch (el.type) {
              case 'point':
                return renderPoint(el, true, i);
              case 'line':
              case 'equationLine':
                return renderLine(el, true, i);
              case 'polygon':
                return renderPolygon(el, true, i);
              case 'circle':
                return renderCircle(el, true, i);
              case 'curve':
                return renderCurve(el, true, i);
              case 'asymptote':
                return renderAsymptote(el, true, i);
              default:
                return null;
            }
          })}
        </g>

        {/* User elements */}
        <g className="elements">
          {elements.map((el, i) => {
            switch (el.type) {
              case 'point':
                return renderPoint(el, false, i);
              case 'line':
              case 'equationLine':
                return renderLine(el, false, i);
              case 'polygon':
                return renderPolygon(el, false, i);
              case 'circle':
                return renderCircle(el, false, i);
              case 'curve':
                return renderCurve(el, false, i);
              case 'asymptote':
                return renderAsymptote(el, false, i);
              default:
                return null;
            }
          })}
        </g>

        {/* Pending points for construction */}
        {renderPendingPoints()}

        {/* Cursor indicator */}
        {renderCursor()}
      </svg>

      {/* Cursor position display */}
      {showCursor && cursorPosition && !readOnly && (
        <Typography
          variant="caption"
          sx={{
            mt: 0.5,
            fontFamily: 'monospace',
            color: colors.grey[300],
          }}
        >
          Position: {formatCoordinate(cursorPosition.x, cursorPosition.y)}
        </Typography>
      )}
    </Box>
  );
};

export default CoordinateGrid;
